import { PREVIEW_SHOWN } from "../products/rows.ts";
import type { RowPreview } from "../products/rows.ts";

export const MENU_PREVIEWS = {
  analytics: "members",
  rewards: "ladder",
  workspace: "roster",
} as const satisfies Record<string, RowPreview>;

export const HEROES = ["overview", "growth", "levels", "team"] as const;

export type Hero = (typeof HEROES)[number];

export const FITTED: readonly Hero[] = ["team"];

export type ShotName = RowPreview | `hero-${Hero}`;

export type Shelf = "card" | "wide" | "hero" | "menu";

export interface Band {
  readonly from: number;
  readonly viewport: number;
}

export const TWO_COLUMNS = 1024;

export const BANDS: Readonly<Record<Shelf, readonly Band[]>> = {
  menu: [{ from: 0, viewport: 1280 }],
  card: [
    { from: 1280, viewport: 1440 },
    { from: TWO_COLUMNS, viewport: 1152 },
    { from: 640, viewport: 800 },
    { from: 0, viewport: 390 },
  ],
  wide: [
    { from: 1280, viewport: 1440 },
    { from: TWO_COLUMNS, viewport: 1152 },
  ],
  hero: [
    { from: 1280, viewport: 1440 },
    { from: 640, viewport: 1024 },
    { from: 0, viewport: 390 },
  ],
};

export type Fit = "card" | "wide" | "single" | "menu";

export interface Placement {
  readonly shelf: "card" | "wide" | "menu";
  readonly index: number;
  readonly band: Band;
}

const shelved = (shelf: "card" | "wide" | "menu"): readonly Placement[] =>
  BANDS[shelf].map((band, index) => ({ shelf, index, band }));

export const placements = (fit: Fit): readonly Placement[] => {
  if (fit === "menu") return shelved("menu");
  const card = shelved("card");
  const single = card.filter((placement) => placement.band.from < TWO_COLUMNS);
  if (fit === "card") return card;
  if (fit === "wide") return [...shelved("wide"), ...single];
  return single;
};

export const fitOf = (preview: RowPreview): Fit =>
  PREVIEW_SHOWN[preview].wide === true ? "wide" : "card";

const SMALLEST_SCALE = 0.8;

export const laidOut = (preview: RowPreview, room: number): number => {
  const { least, compact } = PREVIEW_SHOWN[preview];
  return Math.max(compact !== undefined && room < least * SMALLEST_SCALE ? compact : least, room);
};

export const HERO_LAYOUT = 1152;

export const heroLaidOut = (band: Band, room: number): number =>
  band.from === 0 ? room : Math.max(room, HERO_LAYOUT);

export const SHOT_NAMES: readonly ShotName[] = [
  ...(Object.keys(PREVIEW_SHOWN) as RowPreview[]),
  ...HEROES.map((hero): ShotName => `hero-${hero}`),
];

export const isShotName = (value: string): value is ShotName =>
  (SHOT_NAMES as readonly string[]).includes(value);

export const heroOf = (name: ShotName): Hero | null => {
  const hero = HEROES.find((candidate) => name === `hero-${candidate}`);
  return hero ?? null;
};
